import { Box } from "@mui/material";
import { styled } from "@mui/system";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import CloseIcon from "../../../../../components/icons/CloseIcon";
import { useCreateNewReservation as useCreatePaidReservation } from "../../../../../redux/flows/createReservationFlow";
import { useCreateNewReservation as useCreateRequestOrder } from "../../../../../redux/flows/createVenueReservationFlow";
import { useRemoveReservation } from "../../../../../redux/flows/removeReservationFlow";
import { useUpdateReservation } from "../../../../../redux/flows/updateReservationFlow";
import {
  bookingCreationCompleted as resetApartmentBooking,
  categoryRejectedOnEntrySet,
  enteredViaLinkSet,
} from "../../../../../redux/slice/apartmentRental/apartmentRentalSlice";
import { hasUnusableCategory } from "../../../../home/homeTiles";
import { parseBookingQuery } from "../../url/bookingQuery";
import { clampStep } from "../../url/bookingQuerySchema";
import {
  bookingFlowChanged,
  bookingStepChanged,
  locationDurationRevalidated,
  bookingCreationCompleted as resetBookingCreation,
  setSelectedLocation,
} from "../../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { RootState } from "../../../../../redux/store";
import routes from "../../../../../routes/routes";
import { ServiceTypeSelectorOption } from "../../../../../types/order";
import useServiceStorage from "../../../../../hooks/useServiceStorage";
import { usePrevious } from "../../../../../util/usePrevious";
import GeneralDialog from "../../../../common/GeneralDialog";
import CustomButton from "../../../../layout/components/CustomButton";
import Header from "../../../../layout/components/Header";
import LoaderOverlay from "../../../../layout/components/LoaderOverlay";
import TenantLogo from "../../../../layout/components/TenantLogo";
import ApartmentSortMenu from "../ApartmentSortMenu";
import ApartmentRentalNavigateBackAction from "../../util/ApartmentRentalNavigateBackAction";
import ApartmentRentalNavigateNextAction from "../../util/ApartmentRentalNavigateNextAction";
import BookLocationHeader from "../../util/BookLocationHeader";
import FloatingViewMapButton from "../../util/FloatingViewMapButton";
import { useScrollToTopOnStepChange } from "../../hooks/useScrollToTopOnStepChange";
import { useDirectBookingFlow } from "../../hooks/useDirectBookingFlow";
import { selectObjectDetailsFailed } from "../../hooks/useEnsureApartmentDetails";
import { summaryCtaLabelKey } from "../../hooks/useSummaryCtaLabelKey";
import { BookingCreationStep } from "../../bookingCreationStep";
import { selectObjectSearchPending } from "../../objectSearchPending";
import ObjectSearchFailure from "../ObjectSelectionStep/ObjectSearchFailure";
import CategoryRoomPicker from "../ObjectFirst/CategoryRoomPicker";
import CategoryRoomsSubscription from "../ObjectFirst/CategoryRoomsSubscription";
import { useObjectFirstEntry } from "../../hooks/useObjectFirstEntry";
import SignInFirstGate from "./SignInFirstGate";
import { useIsCoveredBooking } from "../../hooks/useCoveredBooking";
import type { UseCase } from "../../../../../util/useCases";

/**
 * The page shell every room-shaped booking flow runs in (ADR-0026, phase 1
 * step 5) — chrome, step machine, reservation choreography and footer.
 *
 * Merged from `roomRental/BookLocationPage` and
 * `venueReservation/BookLocationPage`, which were ~400 lines each and differed
 * in sixteen places. Every one of those is a field on `BookingFlowShellConfig`
 * rather than a branch, so the shell holds no flow knowledge and adding a
 * ProductCategory does not mean a third copy.
 *
 * ── The merge preserves behaviour, and two arguments show why that is not
 *    automatic ───────────────────────────────────────────────────────────
 *
 * The reservation hooks are called with DIFFERENT arguments per flow, and the
 * difference is load-bearing rather than cosmetic:
 *
 * - `createReservationFlow` branches on the use case's time unit and puts
 *   `useCase` on the request body. Room rental passes `undefined` — NOT
 *   `"apartment"` — so "tidying" that to the flow's own name would change what
 *   a payment request sends.
 * - `createVenueReservationFlow` reads `useCase ?? "venue"`, so the venue
 *   flow's `undefined` genuinely is `"venue"` there.
 *
 * Both are carried as explicit config (`paidReservationUseCase`,
 * `requestOrderUseCase`) holding exactly today's values, rather than derived
 * from a single `useCase` field that would look tidier and be wrong.
 */
export interface BookingFlowShellConfig {
  /** Tenant Services key deciding direct booking vs reservation request. */
  directBookingKey: UseCase;
  serviceType: ServiceTypeSelectorOption;
  /**
   * When the selected service is written to storage. Room rental does it on
   * the way into the summary, venue reservation on mount. Preserved per flow
   * because nothing establishes the two are interchangeable.
   */
  updateServiceOn: "mount" | "summary";
  /** DOM id the scroll-to-top hook targets. */
  stepWrapperId: string;
  /** Exact argument `createReservationFlow` is called with — see above. */
  paidReservationUseCase?: string;
  /** Exact argument `createVenueReservationFlow` is called with — see above. */
  requestOrderUseCase?: string;
  /** Room rental applies its voucher when updating the reservation; venue does not. */
  appliesVoucher: boolean;
  /**
   * Room rental hands the footer its update handler in BOTH flows; venue only
   * in direct booking. Preserved rather than unified.
   */
  passesUpdateHandlerInRequestFlow: boolean;
  headerKeys: { overview: string; personalData: string };
  /** i18n key for the "N objects found" line above the list. */
  objectsFoundKey: string;
  /**
   * The flow's wording for a search the server answered with an error — the
   * same key its selection step shows. Needed here because a deep link can
   * land PAST that step, where only the shell can say the search failed.
   */
  searchErrorMessageKey: string;
}

export interface BookingFlowShellProps {
  config: BookingFlowShellConfig;
  /**
   * The flow's object search, already resolved by the wrapper — this is the
   * one difference that is a different hook rather than a different argument
   * (`useFetchApartments` vs `useFetchVenues`), so it is called at the
   * wrapper's top level and handed down.
   */
  fetchObjects: () => Promise<unknown>;
  /** The flow's own step components, keyed by step. */
  steps: {
    locationSelection: ReactNode;
    objectSelection: ReactNode;
    objectOverview: ReactNode;
    bookingSummary: ReactNode;
    personalData: ReactNode;
    /** Step 5 under direct booking. */
    payment: ReactNode;
    /** Step 5 under the reservation-request flow — same step, other screen. */
    success: ReactNode;
  };
}

const Root = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(1)}px ${theme.spacing(1.5)}px`,
  color: "white",
  height: "100%",
  boxSizing: "border-box",
  backgroundColor: theme.palette.background.default,
  paddingBottom: `calc(${theme.spacing(1)}px + env(safe-area-inset-bottom, 0))`,
  display: "flex",
  flexDirection: "column",
  position: "relative",
}));

const StepWrapper = styled(Box)(({ theme }) => ({
  flex: 1,
  width: "100%",
  maxWidth: 1000,
  margin: "0 auto",
  overflowY: "auto",
  paddingTop: theme.spacing(3),
  // Reserve space for the fixed ActionsWrapper below so the last form
  // field is never hidden behind the Weiter button at any zoom / viewport.
  paddingBottom: 96,
  [theme.breakpoints.up("lg")]: {
    paddingTop: theme.spacing(6),
  },
}));

const ActionsWrapper = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  width: "calc(100% - 42px)",
  maxWidth: 1000,
  margin: "0 auto",
  paddingBottom: theme.spacing(2),
  paddingTop: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  zIndex: 1,
  [theme.breakpoints.up("lg")]: {
    paddingBottom: theme.spacing(2),
  },
}));

// `LogoBox` and `CancelButton` are NOT carried over. Both pages exported them
// and nothing ever imported either — verified across the app, and every other
// flow declares its own copies. Moving dead exports into a new shared module
// would have made them look load-bearing.

const BookingFlowShell = ({
  config,
  fetchObjects,
  steps,
}: BookingFlowShellProps) => {
  const stepWrapperRef = useRef<HTMLElement>();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // #2764 F9: whether the ENTRY link asked for a category that cannot be one.
  // Captured at the first render, because the URL writer drops such a value
  // right after hydration — later reads of the URL no longer see it.
  const location = useLocation();
  const [categoryRejectedOnEntry] = useState(() =>
    hasUnusableCategory(location.search)
  );
  // #2793: did the customer arrive through a link that already carried a
  // search (it lands past the first step)? Only then may a failed search be
  // explained as a problem with the link. Captured now, before hydration and
  // the URL writer have touched anything.
  const [enteredViaLink] = useState(
    () => clampStep(parseBookingQuery(location.search)) > 0
  );

  const [pageDialogOpen, setPageDialogOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  const { locationApartmentList } = useSelector(
    (state: RootState) => state.apartment
  )!;
  const step = useSelector((state: RootState) => state.apartment.step);
  const previousStep = usePrevious(step);
  const roomRentalLocation = useSelector(
    (state: RootState) => state.bookingCreation.roomRentalLocation
  );
  /**
   * The inputs `isLocationDurationValid` reads, selected as PRIMITIVES.
   *
   * Not `orderData` and `roomRentalLocation` as objects, though that reads
   * better: an effect keyed on object identity re-runs whenever anything in
   * the store writes a fresh slice object, dispatches, and re-runs again —
   * the React #185 loop that cost #1458 a follow-up (#1459). The existing
   * shell suite caught exactly that here, because its stub reducer returns a
   * new object per action.
   *
   * Dates are taken as booleans because that is all the validator asks of
   * them, and a `Moment` is an object like any other.
   */
  const useCase = useSelector(
    (state: RootState) => state.bookingCreation.useCase
  );
  const locationCity = useSelector(
    (state: RootState) => state.bookingCreation.roomRentalLocation.city
  );
  const locationDistrict = useSelector(
    (state: RootState) => state.bookingCreation.roomRentalLocation.district
  );
  const hasCheckIn = useSelector(
    (state: RootState) => !!state.bookingCreation.orderData.checkInDate
  );
  const hasCheckOut = useSelector(
    (state: RootState) => !!state.bookingCreation.orderData.checkOutDate
  );
  const startTime = useSelector(
    (state: RootState) => state.bookingCreation.orderData.startTime
  );
  const endTime = useSelector(
    (state: RootState) => state.bookingCreation.orderData.endTime
  );
  const eventType = useSelector(
    (state: RootState) => state.bookingCreation.orderData.eventType
  );
  // One definition, shared with the selection step — see
  // `selectObjectSearchPending`. Written out separately in both files, the two
  // can drift into two stacked spinners or none at all.
  const searchPending = useSelector(selectObjectSearchPending);
  // #2793: a deep link past the selection step still needs the object list,
  // and when that search fails the step it lands on has nothing to show. The
  // selection step renders its own failure; every later one gets it here.
  const searchLoadFailed = useSelector(
    (state: RootState) => state.apartment.apartmentsLoadFailed
  );
  const detailsFailedOnOverview = useSelector(
    (state: RootState) =>
      step === BookingCreationStep.apartmentOverview &&
      selectObjectDetailsFailed(state)
  );
  const searchFailedPastSelection =
    searchLoadFailed &&
    // A failed REFETCH that left a usable list is no reason to hide the step:
    // back to the selection, a refetch blips, forward again — the overview
    // still has everything it needs.
    locationApartmentList.length === 0 &&
    step > BookingCreationStep.apartmentSelection &&
    // Up to the summary (the enum orders personalData BEFORE bookingSummary).
    // Not the payment/confirmation step: a reservation may exist by then, and
    // a search failure is no reason to hide it.
    step <= BookingCreationStep.bookingSummary;
  const selectedObjectId = useSelector(
    (state: RootState) => state.apartment.selectedApartment?.id ?? null
  );
  const voucher = useSelector(
    (state: RootState) => state.bookingCreation.roomRentalVoucher
  );
  const totalPrice = useSelector(
    (state: RootState) => state.bookingCreation.totalPrice
  );
  const additionalServices = useSelector(
    (state: RootState) => state.bookingCreation.additionalServices
  );

  const { updateService } = useServiceStorage(
    "selectedService",
    config.serviceType
  );
  useScrollToTopOnStepChange(stepWrapperRef, step);

  // #3035 (M1): a category whose flow opens on its rooms. Step 0 shows the
  // room list until a room is picked — and known: while the list loads, when
  // it failed, or when a link names a room this caller cannot see, the list is
  // the honest screen. The step machine itself is untouched.
  const objectFirst = useObjectFirstEntry(config.directBookingKey);
  const showRoomPicker =
    objectFirst.isObjectFirst &&
    step === BookingCreationStep.locationSelection &&
    !objectFirst.pickedRoom;

  const directBooking = useDirectBookingFlow(config.directBookingKey);
  const isCovered = useIsCoveredBooking();
  const createPaidReservation = useCreatePaidReservation(
    config.paidReservationUseCase,
    { roomFamilyGroup: true }
  );
  const createRequestOrder = useCreateRequestOrder(config.requestOrderUseCase);
  const removeReservation = useRemoveReservation();

  /**
   * The reservation THIS flow created on entering the summary — the only one
   * the summary's "Weiter" may PATCH and the payment step may charge.
   *
   * It used to be read from `localStorage["new_reservation"]`, which every
   * reservation-creating flow wrote and nothing ever removed: an unfinished
   * reservation for another room from an earlier session was PATCHed, while
   * the payment step (which reads the Redux reservations) found nothing to
   * pay. Nothing else read that key, and no reload path could have used it —
   * the payment step has never been able to pay without the reservation in
   * memory — so it is gone rather than validated.
   *
   * `null` until creation succeeds, and again after the customer steps back
   * (the reservation is released then). With `null`, the summary's "Weiter"
   * retries the creation (`continueFromSummary`).
   */
  const [createdReservationId, setCreatedReservationId] = useState<
    string | null
  >(null);
  const updateReservation = useUpdateReservation(
    createdReservationId,
    config.appliesVoucher ? voucher?.id : undefined,
    additionalServices,
    totalPrice
  );

  /**
   * Which creation attempt is the current one, and which step is on screen —
   * read when a creation result ARRIVES, not when it was started.
   *
   * The steps are browser-history entries and the overlay does not block
   * back/forward, so a result can land after the customer left the summary
   * (the slot would stay held by a reservation nothing pays or releases), or
   * after a back-and-forward started a second creation (two held slots, one
   * orphaned). Such a result is not adopted; its reservation is released.
   */
  const creationAttemptRef = useRef(0);
  // Written during render, not in an effect: a result landing between the
  // commit of a step change and its passive effects must already see it.
  const stepRef = useRef(step);
  stepRef.current = step;

  /**
   * Creates this flow's reservation and adopts it — unless it is stale by
   * the time it arrives (see above). Answers the adopted id, or `null` when
   * creation failed (the creation flow has already told the customer why) or
   * the result was not adopted.
   */
  async function createReservationForSummary(): Promise<string | null> {
    const attempt = ++creationAttemptRef.current;
    setCreatedReservationId(null);
    setLoading(true);
    const created = await createPaidReservation();
    const id = created?.reservationId ?? null;
    const isNewestAttempt = attempt === creationAttemptRef.current;
    // An older attempt leaves the overlay to the newer one still running.
    if (isNewestAttempt) setLoading(false);

    if (
      !isNewestAttempt ||
      stepRef.current !== BookingCreationStep.bookingSummary
    ) {
      if (id) await removeReservation(id, { notifySuccess: false });
      return null;
    }
    setCreatedReservationId(id);
    return id;
  }

  /**
   * The summary's "Weiter". With this flow's reservation in hand it PATCHes
   * it. Without one — creation failed (409, booking-window refusal, network)
   * — it retries the creation first: `updateReservation` alone would only
   * repeat a generic failure on every click and could never succeed. A
   * failed retry has already shown its specific message; the rejection only
   * keeps the footer on the summary, with no second toast.
   */
  async function continueFromSummary() {
    if (!directBooking || createdReservationId) return updateReservation();
    const id = await createReservationForSummary();
    if (!id) {
      throw new Error("Reservation could not be created for the summary");
    }
    return updateReservation(id);
  }

  async function submitReservationRequest() {
    setLoading(true);
    await createRequestOrder();
    setLoading(false);
  }

  useEffect(() => {
    stepWrapperRef.current = document.getElementById(config.stepWrapperId)!;

    // `nextEnabled: false`, not `!!selectedLocation` — deliberately.
    //
    // This effect only OWNS THE STEP. Whether the step is complete is decided
    // by the invariant effect below, which is the single writer of that answer
    // for step 0. The old guess read the location alone, so a deep link with
    // every field hydrated still arrived with a dead "Weiter" (QA #2793), and
    // on a cold mount `selectedLocation` is null anyway — the guess was wrong
    // in both directions. Both effects run before paint, so there is no flash.
    dispatch(
      bookingStepChanged({
        step: BookingCreationStep.locationSelection,
        nextEnabled: false,
      })
    );
    // Before the search effect below, which reads it from the store.
    dispatch(categoryRejectedOnEntrySet(categoryRejectedOnEntry));
    dispatch(enteredViaLinkSet(enteredViaLink));

    // useCase is owned by useBookingFlowLifecycle in the flow's dialog
    // (PR #805 review §2: setUseCase needed cleanup on unmount, lifecycle
    // moved up so init+cleanup live together).
    if (config.updateServiceOn === "mount") {
      updateService(config.serviceType);
    }

    // Intentionally run only once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * At the overview step, Next is enabled exactly when an object is selected.
   *
   * QA #2764 E2: a deep link past step 0 left the CTA permanently disabled.
   * `bookingCreation.nextEnabled` is only ever set by the mount effect above
   * (for step 0) and by the reducers the customer's own input runs through, and
   * `apartmentStepSet` — which is what URL hydration uses to restore a step —
   * does not touch it.
   *
   * The later steps recover by themselves: typing into the customer-data form
   * runs `recomputePersonalData`, which sets it. The overview step has nothing
   * for the customer to do that would, so it is the one that stays stuck — and
   * it is the one the report landed on. Stating the invariant here fixes it
   * without inventing gating rules for the steps that already work.
   */
  useEffect(() => {
    if (step !== BookingCreationStep.apartmentOverview) return;
    if (!selectedObjectId) return;
    dispatch(bookingStepChanged({ step, nextEnabled: true }));
  }, [step, selectedObjectId, dispatch]);

  /**
   * The same invariant for step 0 — QA #2793, symptom 2.
   *
   * A deep link arrives with city, district, both dates, both times and the
   * event type hydrated and visibly filled in, and "Weiter" is dead until the
   * customer touches any one field.
   *
   * `nextEnabled` was recomputed only by the INPUT reducers, i.e. by the
   * customer typing; URL hydration fills the same fields through other paths
   * and never reached the validator. So this is not "the hydration forgot a
   * field" — it is that nothing re-asked the question after hydration wrote.
   *
   * Runs on every change to the inputs the validator reads rather than once,
   * because they do not all arrive in the same tick: the flow's use case comes
   * from the lifecycle, the location and the dates from separate hydration
   * writes. The reducer takes no payload, so this cannot become a second
   * opinion about the answer — it only says "ask again".
   */
  useEffect(() => {
    if (step !== BookingCreationStep.locationSelection) return;
    dispatch(locationDurationRevalidated());
  }, [
    step,
    useCase,
    locationCity,
    locationDistrict,
    hasCheckIn,
    hasCheckOut,
    startTime,
    endTime,
    eventType,
    dispatch,
  ]);

  // Separate from the mount init on purpose: the tenant config can finish
  // loading after this page mounted, flipping `directBooking` — the flow
  // array must follow without re-running the step reset above.
  useEffect(() => {
    dispatch(
      bookingFlowChanged({
        bookingFlow: [
          "locationSelection",
          "apartmentSelection",
          "apartmentOverview",
          "bookingSummary",
          "personalData",
          directBooking ? "payment" : "success",
        ],
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directBooking]);

  useEffect(() => {
    const manageReduxData = async () => {
      // Two reasons to search, one body. Both copies spelled this as an
      // if/else-if with the SAME three lines in each arm; `A ? X : B ? X : -`
      // and `(A || B) ? X : -` select identically, so this is the same
      // behaviour with the duplication removed.
      const needsObjectList =
        // The selection step is about to show the list.
        step === BookingCreationStep.apartmentSelection ||
        // Or URL-state hydration landed directly on step 2+ with an objectId
        // but no fetched list — search so the deferred hydration can resolve.
        (step > BookingCreationStep.apartmentSelection &&
          locationApartmentList.length === 0);

      if (needsObjectList) {
        setLoading(true);
        await fetchObjects();
        setLoading(false);
      }

      if (
        step === BookingCreationStep.apartmentSelection &&
        previousStep === BookingCreationStep.apartmentOverview &&
        roomRentalLocation.district === "all"
      ) {
        dispatch(setSelectedLocation(null));
        setLoading(true);
        await fetchObjects();
        setLoading(false);
      }

      if (
        step === BookingCreationStep.bookingSummary &&
        previousStep === BookingCreationStep.personalData
      ) {
        // The direct-booking flow creates the reservation on entering the
        // summary and pays against it. The request flow makes a single order
        // call on submit (`/orders/from-reservation`) — creating a reservation
        // here as well would double-hold the slot.
        if (directBooking) {
          // A failed creation has already told the customer (the flow
          // notifies); with the id `null`, "Weiter" retries it.
          await createReservationForSummary();
        }
        if (config.updateServiceOn === "summary") {
          updateService(config.serviceType);
        }
      }

      if (
        step === BookingCreationStep.personalData &&
        previousStep === BookingCreationStep.bookingSummary
      ) {
        // Only this flow's own reservation, by its id — never "the last one
        // in the store", which need not be this flow's. Without one (creation
        // failed, or still running — a late result releases itself) there is
        // nothing to release. No success toast: a step back is not "booking
        // deleted".
        if (directBooking && createdReservationId) {
          // Cleared BEFORE the release is awaited: a forward step while the
          // DELETE is on the wire may already have adopted a newer hold, and
          // clearing afterwards would orphan it. Same for the overlay, which
          // belongs to that newer creation once one has started.
          const releasing = createdReservationId;
          const attemptAtRelease = creationAttemptRef.current;
          setCreatedReservationId(null);
          setLoading(true);
          await removeReservation(releasing, { notifySuccess: false });
          if (creationAttemptRef.current === attemptAtRelease) setLoading(false);
        }
      }
    };
    manageReduxData();
    // Dependencies intentionally limited to step changes to avoid repeated
    // fetching loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const headerText = useMemo(() => {
    if (showRoomPicker) return t("book_location.object_first.header");
    switch (step) {
      case BookingCreationStep.locationSelection:
        return t("apartment.location_duration_selection");
      case BookingCreationStep.apartmentOverview:
        return t(config.headerKeys.overview);
      case BookingCreationStep.bookingSummary:
        return t("book_location.booking_summary.header_text");
      case BookingCreationStep.personalData:
        return t(config.headerKeys.personalData);
      case BookingCreationStep.payment:
        // Step 5 is the success screen in the request flow, not payment.
        if (directBooking) return "";
        // A covered booking is confirmed at once — no request waits for
        // review — so "Reservierung gesendet!" would contradict the success
        // text below it (`BookingSuccess`, same hook).
        return isCovered
          ? t("book_location.venue_reservation_overview.booking_confirmed")
          : t("book_location.venue_reservation_overview.reservation_sent");
      default:
        return "";
    }
  }, [step, t, directBooking, isCovered, config.headerKeys, showRoomPicker]);

  const closePage = () => {
    navigate(routes.home);
    dispatch(resetApartmentBooking());
    dispatch(resetBookingCreation());
  };

  const backToBookings = () => {
    dispatch(resetApartmentBooking());
    dispatch(resetBookingCreation());
    navigate(routes.orders);
  };

  const isLastStep = step === BookingCreationStep.payment;

  /**
   * The object search is running and the selection step is on screen.
   *
   * Two things hang off this, both from QA #2764 E10/E11:
   *
   * - the results header must NOT print a count, because that count is `0`
   *   until the search answers and "0 … gefunden" over an empty list IS the
   *   no-results screen. Loading, empty and error are three distinct screens;
   * - this shell must NOT also open its own overlay, because the step already
   *   shows an in-flow spinner. Both used to mount, giving two spinners at the
   *   identical rect for ~600 ms.
   */
  const objectSearchPending =
    step === BookingCreationStep.apartmentSelection && searchPending;

  // The bottom action follows the configured flow. Direct booking keeps the
  // Next button into the payment step; the request flow submits at the summary
  // and offers back-to-bookings on the success screen.
  const renderActions = () => {
    // The failure block's restart is the ONLY way on: a submit or "Weiter"
    // beside it would act on an object that was never loaded. The same holds
    // for an overview whose details failed — its "back to the list" is the way.
    if (searchFailedPastSelection || detailsFailedOnOverview) return null;
    // Picking a room IS the way on; there is nothing for "Weiter" to confirm.
    if (showRoomPicker) return null;
    if (step === BookingCreationStep.apartmentSelection) {
      // No map of a list that never arrived: the failure's restart is the way on.
      return searchLoadFailed ? null : <FloatingViewMapButton />;
    }
    if (!directBooking && isLastStep) {
      return (
        <CustomButton
          isButtonDark={true}
          onClick={backToBookings}
          fullWidth={true}
          style={{ textTransform: "none" }}
          children={t(
            "book_location.venue_reservation_overview.back_to_bookings"
          )}
        />
      );
    }
    if (!directBooking && step === BookingCreationStep.bookingSummary) {
      return (
        <CustomButton
          isButtonDark={true}
          onClick={submitReservationRequest}
          fullWidth={true}
          style={{ textTransform: "none" }}
          children={t(summaryCtaLabelKey(directBooking, isCovered))}
        />
      );
    }
    return (
      !isLastStep && (
        <ApartmentRentalNavigateNextAction
          handleCall={
            directBooking || config.passesUpdateHandlerInRequestFlow
              ? continueFromSummary
              : undefined
          }
        />
      )
    );
  };

  const flow = (
    <Root>
      {objectFirst.subscriptionArgs && (
        <CategoryRoomsSubscription args={objectFirst.subscriptionArgs} />
      )}
      <Header>
        <div className="size-12 absolute left-0">
          <ApartmentRentalNavigateBackAction
            onBackAtStart={
              objectFirst.isObjectFirst && objectFirst.pickedRoom
                ? objectFirst.backToRooms
                : undefined
            }
          />
        </div>
        <TenantLogo />
        {/* `id="closeButton"` comes from the room page. The venue page was the
            only flow of the eight without it; nothing in the app reads the id,
            so this is a DOM-only alignment, not a behaviour change. */}
        <button
          id="closeButton"
          className="size-12 absolute right-0 text-foreground/90"
          onClick={() => setPageDialogOpen(true)}
        >
          <CloseIcon />
        </button>
      </Header>
      <Box sx={{ marginTop: "20px" }}>
        {step === BookingCreationStep.apartmentSelection ? (
          <div
            className="flex items-center justify-between px-4"
            style={{
              flex: 1,
              width: "100%",
              maxWidth: 1000,
              margin: "0 auto",
            }}
          >
            <div>
              {/* Not rendered while the search runs — see `objectSearchPending`.
                  The count is `0` until the answer arrives, and printing it
                  turns a loading screen into a no-results screen (#2764 E10).

                  When it IS rendered, there is no `{list?.length && …}` guard
                  around it. That guard was on the venue page only, and it is
                  `&&` on a NUMBER: with no venues it evaluates to `0` and React
                  renders a bare "0" next to the sort menu. The room page never
                  had it and reads "0 … gefunden"; both flows do that now.

                  Nor after a FAILED search: the list is empty because nothing
                  came back, and "0 gefunden" beside the error message states a
                  result the search never produced. A failure must not read as
                  an empty set. */}
              {/* No count over the ONE room an object-first search is
                  narrowed to (#3035): "12 found" beside a single card would
                  describe a list the customer does not see. */}
              {!objectSearchPending && !searchLoadFailed && !objectFirst.pickedObjectId && (
                <p className="text-sm font-bold text-black">
                  {locationApartmentList?.length}{" "}
                  {t(config.objectsFoundKey, {
                    count: locationApartmentList?.length,
                  })}
                </p>
              )}
            </div>
            {/* Nothing to sort, either, when the search failed. */}
            {!searchLoadFailed && !objectFirst.pickedObjectId && (
              <div>
                <ApartmentSortMenu />
              </div>
            )}
          </div>
        ) : (
          <BookLocationHeader text={headerText} />
        )}
      </Box>

      <StepWrapper id={config.stepWrapperId}>
        {step === BookingCreationStep.locationSelection &&
          (showRoomPicker ? (
            <CategoryRoomPicker
              rooms={objectFirst.rooms}
              loading={objectFirst.roomsLoading}
              failed={objectFirst.roomsFailed}
              onRetry={objectFirst.refetchRooms}
              onPick={objectFirst.pickRoom}
            />
          ) : (
            steps.locationSelection
          ))}
        {step === BookingCreationStep.apartmentSelection && steps.objectSelection}
        {searchFailedPastSelection ? (
          <ObjectSearchFailure
            errorMessageKey={config.searchErrorMessageKey}
          />
        ) : (
          <>
            {step === BookingCreationStep.apartmentOverview &&
              steps.objectOverview}
            {step === BookingCreationStep.bookingSummary &&
              steps.bookingSummary}
            {step === BookingCreationStep.personalData && steps.personalData}
          </>
        )}
        {step === BookingCreationStep.payment &&
          (directBooking ? steps.payment : steps.success)}
      </StepWrapper>

      <ActionsWrapper>{renderActions()}</ActionsWrapper>
      {/* Suppressed while the object search runs: the selection step shows its
          own in-flow spinner then, and mounting both put two spinners at the
          identical rect for ~600 ms (#2764 E11). */}
      {/* Suppressed on the selection step by the STEP, not by the pending
          flag — QA #2791's second symptom.

          `loading` is this component's own `useState`, flipped around
          `await fetchObjects()`; `searchPending` is derived from the store,
          written from inside that call. Two flags for one question, settling
          in different ticks, so completion renders one frame with `loading`
          still true and `searchPending` already false: the overlay mounts,
          animates and vanishes — the stray spinner behind "Zurück".

          Keying on the step removes the second timing source instead of
          racing it. `loading` still covers the other steps, where the overlay
          is the only indicator (URL hydration searches from step 2+). */}
      <LoaderOverlay
        open={loading && step !== BookingCreationStep.apartmentSelection}
      />
      <GeneralDialog
        open={pageDialogOpen}
        onClose={() => setPageDialogOpen(false)}
        onContinue={closePage}
        content={t("use_order.closing_dialog.content")}
      />
    </Root>
  );

  return <SignInFirstGate useCase={config.directBookingKey}>{flow}</SignInFirstGate>;
};

export default BookingFlowShell;
